import React from 'react';
import { MarkdownRenderer } from "../MarkdownRenderer";
import { DataTable } from "../../ui/datatable/datatable";
import { ColumnDef } from "@tanstack/react-table";
import { DefaultToolCallRenderer } from '../ToolRenderers';
import { Badge } from "../../ui/badge";
import { GitBranch, Globe } from 'lucide-react';

// Message interface for the SourceRepositoryToolRenderer
interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string;
}

// ToolRendererProps interface
export interface ToolRendererProps {
    message: Message;
    isToolCall: boolean;
}

// Repository data structure
interface RepositoryInfo {
    repository: string;
    found: boolean;
    environment: string;
}

// Row data for data table
interface RepositoryRow {
    property: string;
    value: string | React.ReactNode;
}

// Tool renderer for source repository info
export function SourceRepositoryToolRenderer({ message, isToolCall }: ToolRendererProps) {
    // We only want to render the response, not the call
    if (!message.tool_call_response_content) {
        // This is a tool call, do the default rendering
        return <DefaultToolCallRenderer message={message} />;
    }

    let repository: RepositoryInfo | null = null;
    try {
        repository = JSON.parse(message.tool_call_response_content);
    } catch (error) {
        console.error('Error parsing source repository response data:', error);
        return null;
    }

    // Validate response
    if (!repository) {
        return null;
    }

    // Convert the repository info to a format suitable for the data table
    const rows: RepositoryRow[] = [
        {
            property: "Repository",
            value: (
                <div className="flex items-center gap-2">
                    <GitBranch className="h-4 w-4" />
                    <a 
                        href={`https://${repository.repository}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                    >
                        {repository.repository}
                        <Globe className="h-3 w-3" />
                    </a>
                </div>
            )
        },
        {
            property: "Found",
            value: (
                <Badge 
                    className={
                        repository.found
                            ? "bg-green-500/20 text-green-500 hover:bg-green-500/20" 
                            : "bg-red-500/20 text-red-500 hover:bg-red-500/20"
                    }
                >
                    {repository.found ? "Yes" : "No"}
                </Badge>
            )
        },
        {
            property: "Environment",
            value: repository.environment || "Not specified"
        }
    ];

    // Define columns for the DataTable
    const columns: ColumnDef<RepositoryRow>[] = [
        {
            id: "property",
            accessorKey: "property",
            header: "Property",
            cell: ({ row }) => <div className="font-medium">{row.original.property}</div>,
        },
        {
            id: "value",
            accessorKey: "value",
            header: "Value",
            cell: ({ row }) => row.original.value,
        }
    ];

    // No filters for this table
    const filters: { filteringColumn: string; title: string; }[] = [];

    return (
        <div className="w-full border rounded">
            <DataTable
                columns={columns}
                data={rows}
                tableTitle="Source Repository"
                filters={filters}
                showPagination={false}
                removeBorder={true}
            />
        </div>
    );
}

// Static method to determine if this renderer should render a message
SourceRepositoryToolRenderer.shouldRender = (message: Message): boolean => {
    return true;
};